import { OrderTemplate } from '../../model/order-template';
import { isEnterKeyEvent } from '../../helpers/utils';
import MessageHelper from '../../helpers/message-helper';
import NotificationHelper from '../../helpers/notification-helper';

export default class OrderTemplateModals {

  private static readonly ORDER_TEMPLATES_URL = `${window.ACC.config.encodedContextPath}/orderTemplates`;
  private static readonly COLORBOX_WIDTH = '500px';

  private readonly duplicateModal: JQuery;
  private readonly duplicateBtnSelector: string = '.js-orderTemplateDuplicateButton';
  private readonly duplicateInputSelector: string = '.js-duplicateNameInput';
  private readonly duplicateErrorContainer: string = '.js-duplicateErrorContainer';

  private readonly createModal: JQuery;
  private readonly createModalBtn: JQuery;
  private readonly createNameInputSelector: string = '.js-createNameInput';
  private readonly createBtnSelector: string = '.js-orderTemplateCreateButton';
  private readonly createErrorContainer: string = '.js-createErrorContainer';

  private readonly createFromCartModal: JQuery;
  private readonly createFromCartModalBtn: JQuery;
  private readonly createFromCartNameInputSelector: string = '.js-createFromCartNameInput';
  private readonly createFromCartBtnSelector: string = '.js-orderTemplateCreateFromCartButton';
  private readonly createFromCartErrorContainer: string = '.js-createFromCartErrorContainer';

  private readonly deleteModal: JQuery;
  private readonly deleteBtnSelector: string = '.js-orderTemplateDeleteButton';
  private readonly deleteErrorContainer: string = '.js-deleteErrorContainer';

  private readonly deleteEntriesModal: JQuery;
  private readonly deleteEntriesBtnSelector: string = '.js-deleteEntriesButton';

  private readonly colorboxCloseButtonSelector: string = '.js-closeColorbox';

  private readonly isOverviewPage: boolean;
  private currentOrderTemplate?: OrderTemplate;

  constructor(private body: JQuery, isOverviewPage = false) {
    this.createModal = body.find('.js-createOrderTemplateModal');
    this.createModalBtn = body.find('.js-createOrderTemplateModalButton');
    this.duplicateModal = body.find('.js-duplicateOrderTemplateModal');
    this.deleteModal = body.find('.js-deleteOrderTemplateModal');
    this.deleteEntriesModal = body.find('.js-deleteEntriesModal');
    this.createFromCartModal = body.find('.js-createFromCartOrderTemplateModal');
    this.createFromCartModalBtn = body.find('.js-createFromCartOrderTemplateModalButton');

    this.isOverviewPage = isOverviewPage;

    this.bindOpenCreateModal();
  }

  bindOpenDeleteEntriesModal(template: OrderTemplate, onEnd: Function): void {
    this.currentOrderTemplate = template;

    window.ACC.colorbox.open('', {
      html: this.deleteEntriesModal.html(),
      width: OrderTemplateModals.COLORBOX_WIDTH,
      onComplete: () => {
        this.bindDeleteEntries(onEnd);
        this.bindCloseColorboxButton();
      },
    });
  }

  bindOpenCreateFromCartModal(): void {
    this.createFromCartModalBtn.on('click', () => {
      window.ACC.colorbox.open('', {
        html: this.createFromCartModal.html(),
        width: OrderTemplateModals.COLORBOX_WIDTH,
        onComplete: () => {
          this.bindCreateFromCartButton();
          this.bindInputKeyUpListener($(this.createNameInputSelector), this.handleCreateFromCart);
          this.bindCloseColorboxButton();
        },
      });
    });
  }

  bindOpenDuplicateModal(template: OrderTemplate): void {
    this.currentOrderTemplate = template;

    window.ACC.colorbox.open('', {
      html: this.duplicateModal.html(),
      width: OrderTemplateModals.COLORBOX_WIDTH,
      onComplete: () => {
        this.bindDuplicateButton();
        this.bindInputKeyUpListener($(this.duplicateInputSelector), this.handleDuplicateEvent);
        this.bindCloseColorboxButton();
      },
    });

  }

  bindOpenDeleteModal(template: OrderTemplate): void {
    this.currentOrderTemplate = template;

    window.ACC.colorbox.open('', {
      html: this.deleteModal.html(),
      width: OrderTemplateModals.COLORBOX_WIDTH,
      onComplete: () => {
        this.bindDeleteButton();
        this.bindCloseColorboxButton();
      },
    });
  }

  private bindOpenCreateModal(): void {
    this.createModalBtn.on('click', () => {
      window.ACC.colorbox.open('', {
        html: this.createModal.html(),
        width: OrderTemplateModals.COLORBOX_WIDTH,
        onComplete: () => {
          this.bindCreateButton();
          this.bindInputKeyUpListener($(this.createNameInputSelector), this.handleCreateEvent);
          this.bindCloseColorboxButton();
        },
      });
    });
  }

  private bindCreateButton(): void {
    $(this.createBtnSelector).on('click', this.handleCreateEvent);
  }

  private bindDeleteButton(): void {
    $(this.deleteBtnSelector).on('click', () => {

      if (this.currentOrderTemplate) {
        this.currentOrderTemplate.delete()
          .done(() => {
            window.location.href = OrderTemplateModals.ORDER_TEMPLATES_URL;
          })
          .fail((err) => {
            $(this.deleteErrorContainer).text(err.responseJSON);
            window.ACC.colorbox.resize();
          });
      }
    });
  }

  private bindDeleteEntries(onEnd: Function): void {
    $(this.deleteEntriesBtnSelector).on('click', () => {
      this.currentOrderTemplate!.clearProductEntries();
      $.colorbox.close();
      onEnd();
    });
  }

  private bindDuplicateButton(): void {
    $(this.duplicateBtnSelector).on('click', this.handleDuplicateEvent);
  }

  private bindCreateFromCartButton(): void {
    $(this.createFromCartBtnSelector).on('click', this.handleCreateFromCart);
  }

  private handleCreateEvent = () => {
    const name = $(this.createNameInputSelector).val() as string;

    OrderTemplate.create(name)
      .done((data, status, xhr) => {
        if (this.isOverviewPage) {
          window.location.reload();
          return;
        }
        window.location.href = xhr.getResponseHeader('Location') as string;
      })
      .fail((err) => {
        $(this.createErrorContainer).text(err.responseText);
        window.ACC.colorbox.resize();
      });
  };

  private handleCreateFromCart = () => {
    const name = $(this.createFromCartNameInputSelector).val() as string;

    OrderTemplate.createFromCart(name)
      .done((data) => {
        $.colorbox.close();
        window.NotificationHelper.clearNotifications();
        window.NotificationHelper.showConfirmation(data.successMessage);

        if (data.hasMaximumNumberOfOrderTemplates) {
          this.createFromCartModalBtn.hide();
        }
      })
      .fail((err) => {
        $(this.createFromCartErrorContainer).text(err.responseJSON);
        window.ACC.colorbox.resize();
      });

  };

  private handleDuplicateEvent = () => {
    const duplicateName = $(this.duplicateInputSelector).val() as string;

    if (this.currentOrderTemplate) {
      this.currentOrderTemplate.duplicate(duplicateName)
        .done(() => {
          if (this.isOverviewPage) {
            window.location.href = OrderTemplateModals.ORDER_TEMPLATES_URL;
            return;
          }

          const message = MessageHelper.getMessageWithAttributes('eparts.order.template.create.success', duplicateName);
          NotificationHelper.Instance.showConfirmation(message);

          window.ACC.colorbox.close();
        })
        .fail((err) => {
          $(this.duplicateErrorContainer).text(err.responseJSON);
          window.ACC.colorbox.resize();
        });
    }
  };

  private bindInputKeyUpListener(element, enterEventCallback: Function): void {
    element.on('keyup', (e: KeyboardEvent) => {
      OrderTemplateModals.removeValidationMessage(element.siblings('.js-inputErrorContainer'));
      if (isEnterKeyEvent(e)) {
        enterEventCallback();
      }
    });
  }

  private static removeValidationMessage(el): void {
    el.text('');
    window.ACC.colorbox.resize();
  }

  private bindCloseColorboxButton(): void {
    $(this.colorboxCloseButtonSelector).on('click', () => {
      $.colorbox.close();
    });
  }

}
